import type { TreeSpecies } from '../../../api/tree-species';

export interface TreeSpeciesRowActions {
  canEdit: boolean;
  canDelete: boolean;
  busy: boolean;
  onEdit: (species: TreeSpecies) => void;
  onToggleActive: (species: TreeSpecies) => void;
  onDelete: (species: TreeSpecies) => void;
}

function usageLabel({ workshopTypeCount, treeReplacementCount }: TreeSpecies): string {
  if (workshopTypeCount === 0 && treeReplacementCount === 0) return 'Unused';
  return `${workshopTypeCount} workshop type(s), ${treeReplacementCount} replacement(s)`;
}

export default function TreeSpeciesRow({
  species,
  actions,
}: {
  species: TreeSpecies;
  actions: TreeSpeciesRowActions;
}) {
  const { canEdit, canDelete, busy } = actions;
  const inUse = species.workshopTypeCount > 0 || species.treeReplacementCount > 0;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-bark-900 dark:text-stone-100">
          {species.name}
          {!species.isActive && (
            <span className="ml-2 rounded bg-stone-100 px-1.5 py-0.5 text-xs text-stone-500 dark:bg-stone-700 dark:text-stone-300">
              Inactive
            </span>
          )}
        </div>
        <div className="text-xs text-stone-500 dark:text-stone-400">
          {species.latinName && <i>{species.latinName} · </i>}
          {usageLabel(species)}
        </div>
      </div>
      {canEdit && (
        <>
          <button
            type="button"
            className="nb-btn nb-btn-ghost"
            disabled={busy}
            onClick={() => actions.onEdit(species)}
            aria-label={`Edit ${species.name}`}
          >
            Edit
          </button>
          <button
            type="button"
            className="nb-btn nb-btn-ghost"
            disabled={busy}
            onClick={() => actions.onToggleActive(species)}
            aria-label={`${species.isActive ? 'Deactivate' : 'Activate'} ${species.name}`}
          >
            {species.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </>
      )}
      {canDelete && (
        <button
          type="button"
          className="nb-btn nb-btn-ghost"
          disabled={busy || inUse}
          title={inUse ? 'In use — deactivate it instead' : undefined}
          onClick={() => actions.onDelete(species)}
          aria-label={`Delete ${species.name}`}
        >
          Delete
        </button>
      )}
    </li>
  );
}
